import { COUNTRY } from "@/shared/constants"

/**
 * País de operación de la Suite (Perú y Biocells Discoveries en Ecuador comparten la misma Suite). Misma regla que el
 * back: todo registro sin país es de Perú, así que para Perú nada cambia. A un usuario que no es admin ni binacional el
 * back le recorta todo a su país (ignora el `country` que mande el front); el admin/superadmin y el binacional (EC-19) ven
 * los dos y pueden filtrar con `country`.
 */
export type SuiteCountry = (typeof COUNTRY)[keyof typeof COUNTRY]

export const SUITE_COUNTRIES: SuiteCountry[] = [COUNTRY.PERU, COUNTRY.ECUADOR]

export const COUNTRY_OPTIONS: { value: SuiteCountry; label: string }[] = [
  { value: COUNTRY.PERU, label: "Perú" },
  { value: COUNTRY.ECUADOR, label: "Ecuador" }
]

/** Valor del selector de país de las pantallas del admin que significa "los dos países" (como antes de Ecuador) */
export const ALL_COUNTRIES = "Todos"
export type CountrySelection = SuiteCountry | typeof ALL_COUNTRIES

export const COUNTRY_SELECTION_OPTIONS: { value: CountrySelection; label: string }[] = [{ value: ALL_COUNTRIES, label: "Todos" }, ...COUNTRY_OPTIONS]

const normalizeText = (value?: string | null): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase()

/** País de un registro o de un perfil ("Perú", "Peru", "ECUADOR"…). Sin país, vacío o desconocido: Perú */
export const countryOf = (country?: string | null): SuiteCountry => (normalizeText(country) === "ecuador" ? COUNTRY.ECUADOR : COUNTRY.PERU)

export const isSameCountry = (a?: string | null, b?: string | null): boolean => countryOf(a) === countryOf(b)

/** Lo que es de `country` (sin país = Perú). Sin `country` devuelve la lista tal cual */
export const filterByCountry = <T extends { country?: string | null }>(items: T[], country?: string | null): T[] => (country ? items.filter((item) => isSameCountry(item.country, country)) : items)

/** Valor del selector ("Todos" | "Perú" | "Ecuador") a parámetro `country` de la API: "Todos" o vacío = sin filtro */
export const countryFromSelection = (selection?: string | null): SuiteCountry | undefined => (!selection || selection === ALL_COUNTRIES ? undefined : countryOf(selection))

export interface LatLng {
  lat: number
  lng: number
}

/** Centros de mapa por país. Objetos fijos a propósito: GoogleMap re-centra cada vez que cambia la referencia de `center` */
export const COUNTRY_MAP_CENTER: Record<SuiteCountry, LatLng> = {
  [COUNTRY.PERU]: { lat: -12.0464, lng: -77.0428 },
  [COUNTRY.ECUADOR]: { lat: -0.1807, lng: -78.4678 }
}

/** Centro del mapa del país: Lima (Perú o sin país, como siempre) o Quito */
export const mapCenterOf = (country?: string | null): LatLng => COUNTRY_MAP_CENTER[countryOf(country)]

/** Código de región de Google (Places/Geocoding): "pe" o "ec" */
export const regionCodeOf = (country?: string | null): "pe" | "ec" => (countryOf(country) === COUNTRY.ECUADOR ? "ec" : "pe")

/**
 * Respuesta de `GET /users/me/country`: el país principal del usuario y si puede ver los dos (admin, superadmin o
 * binacional, EC-19). `binational` indica si lo es por la marca de su perfil: no es admin, pero en todo lo que es país
 * (columna y filtro País, selector de país, mapa por país, crear eligiendo país) se comporta como uno.
 */
export interface CountryScope {
  country: SuiteCountry
  canSeeAllCountries: boolean
  binational: boolean
}

/** Mientras carga `/users/me/country` (o si falla): el país del perfil guardado en la sesión, el rol admin y la marca binacional */
export const fallbackCountryScope = (userCountry: string | null | undefined, isAdmin: boolean, binational = false): CountryScope => ({
  country: countryOf(userCountry),
  canSeeAllCountries: isAdmin || binational,
  binational
})

/** Normaliza lo que devuelve el back (tolera valores sin tilde o faltantes) */
export const normalizeCountryScope = (raw: Partial<CountryScope> | null | undefined, fallback: CountryScope): CountryScope => {
  if (!raw || typeof raw !== "object") return fallback
  return {
    country: raw.country ? countryOf(raw.country) : fallback.country,
    canSeeAllCountries: typeof raw.canSeeAllCountries === "boolean" ? raw.canSeeAllCountries : fallback.canSeeAllCountries,
    binational: typeof raw.binational === "boolean" ? raw.binational : fallback.binational
  }
}

/**
 * País con el que arranca un registro nuevo cuando quien ve los dos países todavía no eligió: el binacional parte en su
 * país principal; el admin sigue partiendo en Perú, como siempre.
 */
export const defaultCreateCountry = (scope: CountryScope): SuiteCountry => (scope.binational ? scope.country : COUNTRY.PERU)

/** País de un registro nuevo: el que eligió quien ve los dos países o, si no eligió, el de `defaultCreateCountry` */
export const createCountry = (scope: CountryScope, chosen?: string | null): SuiteCountry => (chosen ? countryOf(chosen) : defaultCreateCountry(scope))

/**
 * `country` a mandar en un listado: solo quien ve los dos países filtra (lo que eligió; "Todos" = sin filtro). A los
 * demás no se les manda nada: el back ya les recorta todo a su país, y así sus pedidos quedan iguales que antes.
 */
export const countryQueryParam = (scope: CountryScope, selection?: string | null): SuiteCountry | undefined => (scope.canSeeAllCountries ? countryFromSelection(selection) : undefined)

/** País con el que se trabaja en pantalla (centro del mapa, oficina…): el elegido por el admin, o el del usuario */
export const workingCountry = (scope: CountryScope, selection?: string | null): SuiteCountry | undefined => (scope.canSeeAllCountries ? countryFromSelection(selection) : scope.country)
